import { create } from 'zustand';
import { STATE_SCHEMA_VERSION, type AnalysisConfig, type Building, type MetricKey, type Placement } from '../core/domain/types';
import { defaultConfig, sanitizeConfig } from '../core/domain/config';
import type {
  CellInfo,
  DataMode,
  DemoManifestCell,
  FromWorker,
  QueueState,
  ResultEntry,
  ToWorker,
  WorkerReply,
  WorkerRequest,
} from '../core/worker/protocol';
import { MAX_BUILDINGS, MAX_LABEL, MODE_KEY, clearState, loadState, saveState, type PersistedState } from './persist';
import { fetchGwr } from '../geo/services';

export interface DemoManifest {
  retrievedAt: string;
  dataset: string;
  licence: string;
  buildings: {
    id: string;
    label: string;
    address: string;
    egid: string | null;
    lat: number;
    lon: number;
    e: number;
    n: number;
    elevation: number;
    cellLat: number;
    cellLon: number;
    timezone: string;
    cellKey: string;
    lapseCheck: { hours: number; maxAbsDiffK: number; meanAbsDiffK: number };
  }[];
  cells: (DemoManifestCell & { missing: number; conflicts: number })[];
}

export type NoticeKind = 'info' | 'warn' | 'danger';
export interface Notice {
  id: number;
  kind: NoticeKind;
  title: string;
  text?: string;
}

export type View = 'portfolio' | 'uebersicht' | 'detail' | 'zukunft' | 'vergleich' | 'annahmen' | 'bericht';

interface AppState {
  mode: DataMode;
  booted: boolean;
  buildings: Building[];
  placements: Placement[];
  config: AnalysisConfig;
  focusId: string | null;
  compareIds: string[];
  metric: MetricKey;
  detailYear: number | null;
  version: number;
  ready: boolean;
  keyed: boolean;
  cacheAvailable: boolean;
  queue: QueueState | null;
  results: Record<string, ResultEntry>;
  resultsProgress: { done: number; total: number; computeMs: number } | null;
  cells: Record<string, CellInfo>;
  placementFailures: Record<string, string>;
  notices: Notice[];
  savedAt: string | null;
  saveError: boolean;
  demo: DemoManifest | null;
  fatal: string | null;

  boot: () => Promise<void>;
  setMode: (m: DataMode) => Promise<void>;
  addBuildings: (bs: Building[]) => { added: number; skipped: number; overLimit: number };
  removeBuilding: (id: string) => void;
  removeBuildings: (ids: string[]) => void;
  renameBuilding: (id: string, label: string) => void;
  setFocus: (id: string) => void;
  toggleCompare: (id: string) => boolean;
  setCompare: (ids: string[]) => void;
  setMetric: (k: MetricKey) => void;
  setDetailYear: (y: number | null) => void;
  updateConfig: (patch: Partial<AnalysisConfig>) => void;
  resetConfig: () => void;
  replaceState: (st: PersistedState) => void;
  resetPortfolio: () => void;
  notify: (kind: NoticeKind, title: string, text?: string) => void;
  dismiss: (id: number) => void;
}

// ------------------------------------------------------------ Worker-Anbindung
let worker: Worker | null = null;
let reqSeq = 1;
const pending = new Map<number, { resolve: (v: WorkerReply) => void; reject: (e: Error) => void }>();
let noticeSeq = 1;

export function request<T extends WorkerReply>(req: WorkerRequest): Promise<T> {
  if (!worker) return Promise.reject(new Error('Rechenkern nicht gestartet'));
  const reqId = reqSeq++;
  return new Promise<T>((resolve, reject) => {
    pending.set(reqId, { resolve: resolve as (v: WorkerReply) => void, reject });
    send({ type: 'request', reqId, req });
  });
}

function send(m: ToWorker) {
  worker?.postMessage(m);
}

export const workerControl = {
  pause: () => send({ type: 'pause' }),
  resume: () => send({ type: 'resume' }),
  retry: () => send({ type: 'retryFailed' }),
};

function startWorker(mode: DataMode, demo: DemoManifest | null) {
  worker?.terminate();
  for (const p of pending.values()) p.reject(new Error('Rechenkern neu gestartet'));
  pending.clear();
  worker = new Worker(new URL('../core/worker/analysis.worker.ts', import.meta.url), { type: 'module', name: 'klimaraum-analyse' });
  worker.onmessage = (ev: MessageEvent<FromWorker>) => onWorkerMessage(ev.data);
  worker.onerror = (ev) => useApp.setState({ fatal: `Rechenkern-Fehler: ${ev.message}` });
  send({
    type: 'init',
    mode,
    apiBase: '/api',
    nowMs: Date.now(),
    demoCells: demo?.cells,
    demoRetrievedAt: demo?.retrievedAt,
  });
}

function pushState() {
  const s = useApp.getState();
  send({
    type: 'setState',
    buildings: s.buildings,
    placements: s.placements,
    config: s.config,
    version: s.version,
    focusId: s.focusId,
    compareIds: s.compareIds,
  });
}

function onWorkerMessage(m: FromWorker) {
  const set = useApp.setState;
  switch (m.type) {
    case 'ready':
      set({ ready: true, keyed: m.keyed, cacheAvailable: m.cacheAvailable });
      pushState();
      break;
    case 'placements': {
      const s = useApp.getState();
      const byId = new Map(s.placements.map((p) => [p.buildingId, p]));
      for (const p of m.placements) byId.set(p.buildingId, p);
      const failures: Record<string, string> = {};
      for (const f of m.failures) failures[f.buildingId] = f.reason;
      set({ placements: [...byId.values()], placementFailures: failures });
      scheduleSave();
      break;
    }
    case 'queue':
      set({ queue: m.queue });
      break;
    case 'results': {
      const s = useApp.getState();
      if (m.version !== s.version) break;
      if (m.results.length) {
        const next = { ...s.results };
        for (const r of m.results) next[r.buildingId] = r;
        set({ results: next, resultsProgress: { done: m.done, total: m.total, computeMs: m.computeMs } });
      } else {
        set({ resultsProgress: { done: m.done, total: m.total, computeMs: m.computeMs } });
      }
      break;
    }
    case 'cells': {
      const cells: Record<string, CellInfo> = {};
      for (const c of m.cells) cells[c.key] = c;
      set({ cells });
      break;
    }
    case 'reply': {
      const p = pending.get(m.reqId);
      if (!p) break;
      pending.delete(m.reqId);
      if (m.ok) p.resolve(m.data);
      else p.reject(new Error(m.error));
      break;
    }
    case 'fatal':
      set({ fatal: m.message });
      break;
  }
}

// ------------------------------------------------------------ Speichern
let saveTimer: ReturnType<typeof setTimeout> | null = null;
function saveNow() {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = null;
  const s = useApp.getState();
  if (!s.booted) return;
  const savedAt = new Date().toISOString();
  const ok = saveState(s.mode, persistable(s, savedAt));
  useApp.setState({ savedAt: ok ? savedAt : s.savedAt, saveError: !ok });
}
function scheduleSave() {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(saveNow, 400);
}
if (typeof window !== 'undefined') {
  // Ausstehende Änderungen beim Verlassen oder Verbergen der Seite sofort sichern
  window.addEventListener('pagehide', () => saveTimer && saveNow());
  document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && saveTimer && saveNow());
}

export function persistable(s: Pick<AppState, 'buildings' | 'placements' | 'config' | 'focusId' | 'compareIds' | 'metric' | 'detailYear'>, savedAt: string): PersistedState {
  return {
    schemaVersion: STATE_SCHEMA_VERSION,
    buildings: s.buildings,
    placements: s.placements,
    config: s.config,
    focusId: s.focusId,
    compareIds: s.compareIds,
    metric: s.metric,
    detailYear: s.detailYear,
    savedAt,
  };
}

// ------------------------------------------------------------ Startportfolio
export const EXAMPLE_BUILDING: Building = {
  id: 'beispiel-eth-hg',
  label: 'ETH Zürich, Hauptgebäude',
  address: 'Rämistrasse 101, 8006 Zürich',
  egid: '3169989',
  country: 'CH',
  lat: 47.37648,
  lon: 8.548268,
  lv95: { e: 2683682.2, n: 1247562.3 },
  elevation: { value: 453.5, source: 'swissALTI3D' },
  timezone: 'Europe/Zurich',
  geocoder: 'geo.admin.ch',
  addedAt: '2026-09-27T00:00:00.000Z',
};

function demoPortfolio(demo: DemoManifest): { buildings: Building[]; placements: Placement[] } {
  const buildings: Building[] = demo.buildings.map((b) => ({
    id: b.id,
    label: b.label,
    address: b.address.replace(/ (\d{4}) /, ', $1 '),
    egid: b.egid ?? undefined,
    country: 'CH',
    lat: b.lat,
    lon: b.lon,
    lv95: { e: b.e, n: b.n },
    elevation: { value: b.elevation, source: 'swissALTI3D' },
    timezone: b.timezone,
    geocoder: 'geo.admin.ch',
    addedAt: demo.retrievedAt,
  }));
  const placements: Placement[] = demo.buildings.map((b) => ({
    buildingId: b.id,
    model: 'era5_land',
    cellKey: b.cellKey,
    cellLat: b.cellLat,
    cellLon: b.cellLon,
    timezone: b.timezone,
    targetElevation: b.elevation,
    targetElevationSource: 'swissALTI3D',
    discoveredAt: demo.retrievedAt,
  }));
  return { buildings, placements };
}

async function loadDemoManifest(): Promise<DemoManifest | null> {
  try {
    const r = await fetch('/demo/manifest.json');
    if (!r.ok) return null;
    return (await r.json()) as DemoManifest;
  } catch {
    return null;
  }
}

function initialFor(mode: DataMode, demo: DemoManifest | null): PersistedState {
  const cfg = defaultConfig();
  const base = mode === 'demo' && demo ? demoPortfolio(demo) : { buildings: [EXAMPLE_BUILDING], placements: [] };
  return {
    schemaVersion: STATE_SCHEMA_VERSION,
    ...base,
    config: cfg,
    focusId: base.buildings[0]?.id ?? null,
    compareIds: mode === 'demo' ? base.buildings.map((b) => b.id) : [],
    metric: 'cdd',
    detailYear: null,
    savedAt: new Date().toISOString(),
  };
}

// GWR-Merkmale nachladen (gedrosselt), nur für Schweizer Gebäude mit EGID
let enriching = false;
/** EGIDs ohne Treffer in dieser Sitzung; nicht erneut abfragen, die übrigen Gebäude aber weiter ergänzen. */
const gwrMissing = new Set<string>();
async function enrichGwr() {
  if (enriching) return;
  enriching = true;
  try {
    for (;;) {
      const s = useApp.getState();
      if (s.mode !== 'live') break;
      const todo = s.buildings.filter((b) => b.egid && !b.gwr && !gwrMissing.has(b.egid)).slice(0, 4);
      if (!todo.length) break;
      const infos = await Promise.all(todo.map((b) => fetchGwr(b.egid!)));
      todo.forEach((b, i) => infos[i] === null && gwrMissing.add(b.egid!));
      const found = new Map(todo.flatMap((b, i) => (infos[i] ? [[b.id, infos[i]!] as const] : [])));
      if (found.size) {
        useApp.setState({ buildings: useApp.getState().buildings.map((b) => (found.has(b.id) ? { ...b, gwr: found.get(b.id) } : b)) });
        scheduleSave();
      }
    }
  } finally {
    enriching = false;
  }
}

// ------------------------------------------------------------ Store
export const useApp = create<AppState>((set, get) => ({
  mode: 'live',
  booted: false,
  buildings: [],
  placements: [],
  config: defaultConfig(),
  focusId: null,
  compareIds: [],
  metric: 'cdd',
  detailYear: null,
  version: 1,
  ready: false,
  keyed: false,
  cacheAvailable: false,
  queue: null,
  results: {},
  resultsProgress: null,
  cells: {},
  placementFailures: {},
  notices: [],
  savedAt: null,
  saveError: false,
  demo: null,
  fatal: null,

  async boot() {
    let mode: DataMode = 'live';
    try {
      const m = localStorage.getItem(MODE_KEY);
      if (m === 'demo') mode = 'demo';
    } catch {
      /* ohne Speicher: live */
    }
    const params = new URLSearchParams(location.search);
    if (params.get('modus') === 'demo') mode = 'demo';
    if (params.get('modus') === 'live') mode = 'live';
    await get().setMode(mode);
  },

  async setMode(mode) {
    // Ausstehende Änderungen des bisherigen Modus sichern, bevor dessen Zustand ersetzt wird
    if (saveTimer) saveNow();
    const demo = mode === 'demo' ? (get().demo ?? (await loadDemoManifest())) : get().demo;
    if (mode === 'demo' && !demo) {
      get().notify('danger', 'Offline-Demo nicht verfügbar', 'Der Datensnapshot konnte nicht geladen werden.');
      return;
    }
    const loaded = loadState(mode);
    let st: PersistedState;
    if (loaded.ok) st = loaded.state;
    else {
      st = initialFor(mode, demo);
      if (loaded.reason === 'corrupt' || loaded.reason === 'incompatible') {
        get().notify(
          'warn',
          'Gespeicherte Analyse nicht geladen',
          `Der lokal gespeicherte Zustand war ${loaded.reason === 'corrupt' ? 'beschädigt' : 'inkompatibel'} (${loaded.detail}). Er wurde gesichert und nicht verwendet; die App startet mit dem Standardzustand.`,
        );
      }
    }
    try {
      localStorage.setItem(MODE_KEY, mode);
    } catch {
      /* ignorieren */
    }
    set({
      mode,
      demo,
      booted: true,
      buildings: st.buildings,
      placements: st.placements,
      config: st.config,
      focusId: st.focusId,
      compareIds: st.compareIds,
      metric: st.metric,
      detailYear: st.detailYear,
      savedAt: loaded.ok ? st.savedAt : null,
      version: get().version + 1,
      ready: false,
      queue: null,
      results: {},
      resultsProgress: null,
      cells: {},
      placementFailures: {},
      fatal: null,
    });
    startWorker(mode, demo);
    scheduleSave();
    if (mode === 'live') void enrichGwr();
  },

  addBuildings(bs) {
    const s = get();
    const existing = new Set(s.buildings.map((b) => (b.egid ? `egid:${b.egid}` : `ll:${b.lat.toFixed(5)},${b.lon.toFixed(5)}`)));
    const fresh: Building[] = [];
    let skipped = 0;
    let overLimit = 0;
    for (const b of bs) {
      const k = b.egid ? `egid:${b.egid}` : `ll:${b.lat.toFixed(5)},${b.lon.toFixed(5)}`;
      if (existing.has(k)) {
        skipped++;
        continue;
      }
      if (s.buildings.length + fresh.length >= MAX_BUILDINGS) {
        overLimit++;
        continue;
      }
      existing.add(k);
      fresh.push(b.label.length > MAX_LABEL ? { ...b, label: b.label.slice(0, MAX_LABEL) } : b);
    }
    if (fresh.length) {
      set({ buildings: [...s.buildings, ...fresh], focusId: s.focusId ?? fresh[0].id });
      pushState();
      scheduleSave();
      void enrichGwr();
    }
    return { added: fresh.length, skipped, overLimit };
  },

  removeBuilding(id) {
    get().removeBuildings([id]);
  },

  removeBuildings(ids) {
    const s = get();
    const drop = new Set(ids);
    const buildings = s.buildings.filter((b) => !drop.has(b.id));
    const results = { ...s.results };
    ids.forEach((id) => delete results[id]);
    set({
      buildings,
      placements: s.placements.filter((p) => !drop.has(p.buildingId)),
      compareIds: s.compareIds.filter((c) => !drop.has(c)),
      focusId: s.focusId && !drop.has(s.focusId) ? s.focusId : (buildings[0]?.id ?? null),
      results,
    });
    pushState();
    scheduleSave();
  },

  renameBuilding(id, label) {
    const clean = label.trim().slice(0, MAX_LABEL);
    if (!clean) return;
    set({ buildings: get().buildings.map((b) => (b.id === id ? { ...b, label: clean } : b)) });
    scheduleSave();
  },

  setFocus(id) {
    if (id === get().focusId) return;
    set({ focusId: id });
    pushState();
    scheduleSave();
  },

  toggleCompare(id) {
    const s = get();
    if (s.compareIds.includes(id)) {
      set({ compareIds: s.compareIds.filter((x) => x !== id) });
    } else {
      if (s.compareIds.length >= 5) return false;
      set({ compareIds: [...s.compareIds, id] });
    }
    pushState();
    scheduleSave();
    return true;
  },

  setCompare(ids) {
    set({ compareIds: ids.slice(0, 5) });
    pushState();
    scheduleSave();
  },

  setMetric(k) {
    set({ metric: k });
    scheduleSave();
  },

  setDetailYear(y) {
    set({ detailYear: y });
    scheduleSave();
  },

  updateConfig(patch) {
    const s = get();
    const next = sanitizeConfig({ ...s.config, ...patch, hints: { ...s.config.hints, ...(patch.hints ?? {}) } });
    if (JSON.stringify(next) === JSON.stringify(s.config)) return;
    set({ config: next, version: s.version + 1 });
    pushState();
    scheduleSave();
  },

  resetConfig() {
    const s = get();
    const d = defaultConfig();
    set({ config: { ...d, model: s.config.model, periodStart: s.config.periodStart, periodEnd: s.config.periodEnd }, version: s.version + 1 });
    pushState();
    scheduleSave();
  },

  replaceState(st) {
    set({
      buildings: st.buildings,
      placements: st.placements,
      config: sanitizeConfig(st.config),
      focusId: st.focusId,
      compareIds: st.compareIds,
      metric: st.metric,
      detailYear: st.detailYear,
      version: get().version + 1,
      results: {},
    });
    pushState();
    scheduleSave();
  },

  resetPortfolio() {
    const s = get();
    clearState(s.mode);
    const st = initialFor(s.mode, s.demo);
    get().replaceState(st);
  },

  notify(kind, title, text) {
    const id = noticeSeq++;
    set({ notices: [...get().notices, { id, kind, title, text }] });
    if (kind === 'info') setTimeout(() => get().dismiss(id), 6000);
  },

  dismiss(id) {
    set({ notices: get().notices.filter((n) => n.id !== id) });
  },
}));

// ------------------------------------------------------------ Selektoren
export function placementFor(s: Pick<AppState, 'placements' | 'config'>, id: string | null): Placement | undefined {
  if (!id) return undefined;
  return s.placements.find((p) => p.buildingId === id && p.model === s.config.model);
}

/** Ergebnis nur, wenn es zur aktuellen Zelle gehört. */
export function resultFor(s: Pick<AppState, 'results' | 'placements' | 'config'>, id: string | null): ResultEntry | undefined {
  if (!id) return undefined;
  const r = s.results[id];
  const p = placementFor(s, id);
  if (!r || !p || r.cellKey !== p.cellKey) return undefined;
  return r;
}

export function isStale(s: Pick<AppState, 'version'>, r: ResultEntry | undefined): boolean {
  return !!r && r.version !== s.version;
}

if (typeof window !== 'undefined') {
  (window as unknown as { __klimaraum?: unknown }).__klimaraum = { useApp, request };
}
